import { useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Building2 } from "lucide-react";

// Job descriptions for each role with title and bullet points
const jobDescriptions = {
  // Revenue Department roles
  tahsildar: {
    title: { en: "Tahsildar", kn: "ತಹಶೀಲ್ದಾರ್" },
    points: {
      en: [
        "Head of Taluk administration",
        "Reviews and approves applications",
        "Issues orders",
        "Oversees revenue collection",
        "Manages land records"
      ],
      kn: [
        "ತಾಲ್ಲೂಕು ಆಡಳಿತದ ಮುಖ್ಯಸ್ಥ",
        "ಅರ್ಜಿಗಳನ್ನು ಪರಿಶೀಲಿಸುತ್ತಾರೆ ಮತ್ತು ಅನುಮೋದಿಸುತ್ತಾರೆ",
        "ಆದೇಶಗಳನ್ನು ಹೊರಡಿಸುತ್ತಾರೆ",
        "ಕಂದಾಯ ಸಂಗ್ರಹಣೆಯನ್ನು ಮೇಲ್ವಿಚಾರಣೆ ಮಾಡುತ್ತಾರೆ",
        "ಭೂದಾಖಲೆಗಳನ್ನು ನಿರ್ವಹಿಸುತ್ತಾರೆ"
      ]
    }
  },
  caseWorker: {
    title: { en: "Case Worker", kn: "ಕೇಸ್ ವರ್ಕರ್" },
    points: {
      en: [
        "Handles individual application cases",
        "Prepares case files",
        "Coordinates with other officials",
        "Tracks application progress"
      ],
      kn: [
        "ವೈಯಕ್ತಿಕ ಅರ್ಜಿ ಪ್ರಕರಣಗಳನ್ನು ನಿರ್ವಹಿಸುತ್ತಾರೆ",
        "ಕೇಸ್ ಫೈಲ್‌ಗಳನ್ನು ತಯಾರಿಸುತ್ತಾರೆ",
        "ಇತರ ಅಧಿಕಾರಿಗಳೊಂದಿಗೆ ಸಮನ್ವಯ ಮಾಡುತ್ತಾರೆ",
        "ಅರ್ಜಿ ಪ್ರಗತಿಯನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡುತ್ತಾರೆ"
      ]
    }
  },
  sheristedar: {
    title: { en: "Sheristedar", kn: "ಶೆರಿಸ್ತೆದಾರ್" },
    points: {
      en: [
        "Office superintendent",
        "Responsible for record maintenance",
        "Tracks file movement",
        "Handles administrative coordination"
      ],
      kn: [
        "ಕಚೇರಿ ಅಧೀಕ್ಷಕ",
        "ದಾಖಲೆ ನಿರ್ವಹಣೆಗೆ ಜವಾಬ್ದಾರ",
        "ಫೈಲ್ ಚಲನೆಯನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡುತ್ತಾರೆ",
        "ಆಡಳಿತಾತ್ಮಕ ಸಮನ್ವಯವನ್ನು ನಿರ್ವಹಿಸುತ್ತಾರೆ"
      ]
    }
  },
  ri: {
    title: { en: "Revenue Inspector", kn: "ಕಂದಾಯ ಇನ್ಸ್‌ಪೆಕ್ಟರ್" },
    points: {
      en: [
        "Conducts field inspections",
        "Verifies land boundaries",
        "Prepares survey reports",
        "Assists in dispute resolution"
      ],
      kn: [
        "ಕ್ಷೇತ್ರ ತಪಾಸಣೆ ನಡೆಸುತ್ತಾರೆ",
        "ಭೂಮಿ ಗಡಿಗಳನ್ನು ಪರಿಶೀಲಿಸುತ್ತಾರೆ",
        "ಸಮೀಕ್ಷೆ ವರದಿಗಳನ್ನು ತಯಾರಿಸುತ್ತಾರೆ",
        "ವಿವಾದ ಪರಿಹಾರದಲ್ಲಿ ಸಹಾಯ ಮಾಡುತ್ತಾರೆ"
      ]
    }
  },
  vao: {
    title: { en: "Village Administrative Office", kn: "ಗ್ರಾಮ ಆಡಳಿತ ಕಚೇರಿ" },
    points: {
      en: [
        "Maintains village-level land records",
        "Issues RTCs",
        "Collects revenue",
        "First-point contact for farmers"
      ],
      kn: [
        "ಗ್ರಾಮ ಮಟ್ಟದ ಭೂದಾಖಲೆಗಳನ್ನು ನಿರ್ವಹಿಸುತ್ತಾರೆ",
        "RTC ಗಳನ್ನು ನೀಡುತ್ತಾರೆ",
        "ಕಂದಾಯ ಸಂಗ್ರಹಿಸುತ್ತಾರೆ",
        "ರೈತರಿಗೆ ಮೊದಲ ಸಂಪರ್ಕ ಬಿಂದು"
      ]
    }
  },
  vaReport: {
    title: { en: "VA Report", kn: "VA ವರದಿ" },
    points: {
      en: [
        "Contains land details",
        "Includes ownership records",
        "Preliminary verification of application"
      ],
      kn: [
        "ಭೂಮಿ ವಿವರಗಳನ್ನು ಒಳಗೊಂಡಿದೆ",
        "ಮಾಲೀಕತ್ವ ದಾಖಲೆಗಳನ್ನು ಒಳಗೊಂಡಿದೆ",
        "ಅರ್ಜಿಯ ಪ್ರಾಥಮಿಕ ಪರಿಶೀಲನೆ"
      ]
    }
  },
  riReport: {
    title: { en: "Revenue Inspector", kn: "ಕಂದಾಯ ಇನ್ಸ್‌ಪೆಕ್ಟರ್" },
    points: {
      en: [
        "Detailed report after field inspection",
        "Includes measurements",
        "Contains observations and recommendations"
      ],
      kn: [
        "ಕ್ಷೇತ್ರ ತಪಾಸಣೆ ನಂತರ ವಿವರವಾದ ವರದಿ",
        "ಮಾಪನಗಳನ್ನು ಒಳಗೊಂಡಿದೆ",
        "ಅವಲೋಕನಗಳು ಮತ್ತು ಶಿಫಾರಸುಗಳನ್ನು ಒಳಗೊಂಡಿದೆ"
      ]
    }
  },
  talukOffice: {
    title: { en: "Taluk Office (TAPPAL)", kn: "ತಾಲ್ಲೂಕು ಕಚೇರಿ (ತಪ್ಪಾಲ್)" },
    points: {
      en: [
        "Central receiving point",
        "Receives all applications and documents",
        "Routes files to appropriate officials"
      ],
      kn: [
        "ಕೇಂದ್ರ ಸ್ವೀಕೃತಿ ಸ್ಥಳ",
        "ಎಲ್ಲಾ ಅರ್ಜಿಗಳು ಮತ್ತು ದಾಖಲೆಗಳನ್ನು ಸ್ವೀಕರಿಸುತ್ತದೆ",
        "ಸೂಕ್ತ ಅಧಿಕಾರಿಗಳಿಗೆ ಫೈಲ್‌ಗಳನ್ನು ರವಾನಿಸುತ್ತದೆ"
      ]
    }
  },
  fieldSurveyReport: {
    title: { en: "Field Survey Report", kn: "ಕ್ಷೇತ್ರ ಸಮೀಕ್ಷೆ ವರದಿ" },
    points: {
      en: [
        "Detailed survey report",
        "Contains measurements and boundaries",
        "Physical verification of land"
      ],
      kn: [
        "ವಿವರವಾದ ಸಮೀಕ್ಷೆ ವರದಿ",
        "ಮಾಪನಗಳು ಮತ್ತು ಗಡಿಗಳನ್ನು ಒಳಗೊಂಡಿದೆ",
        "ಭೂಮಿಯ ಭೌತಿಕ ಪರಿಶೀಲನೆ"
      ]
    }
  },
  verification: {
    title: { en: "Verification", kn: "ಪರಿಶೀಲನೆ" },
    points: {
      en: [
        "Cross-checking of all reports",
        "Ensures accuracy and completeness",
        "Final review before approval"
      ],
      kn: [
        "ಎಲ್ಲಾ ವರದಿಗಳ ಪರಸ್ಪರ ಪರಿಶೀಲನೆ",
        "ನಿಖರತೆ ಮತ್ತು ಸಂಪೂರ್ಣತೆಯನ್ನು ಖಚಿತಪಡಿಸುತ್ತದೆ",
        "ಅನುಮೋದನೆಯ ಮೊದಲು ಅಂತಿಮ ಪರಿಶೀಲನೆ"
      ]
    }
  },
  workDone: {
    title: { en: "Work Done - Tahsildar Order", kn: "ಕೆಲಸ ಮುಗಿದಿದೆ - ತಹಶೀಲ್ದಾರ್ ಆದೇಶ" },
    points: {
      en: [
        "Final stage of application",
        "All processes completed successfully",
        "Tahsildar issues the final order",
        "Application approved"
      ],
      kn: [
        "ಅರ್ಜಿಯ ಅಂತಿಮ ಹಂತ",
        "ಎಲ್ಲಾ ಪ್ರಕ್ರಿಯೆಗಳು ಯಶಸ್ವಿಯಾಗಿ ಪೂರ್ಣಗೊಂಡಿವೆ",
        "ತಹಶೀಲ್ದಾರ್ ಅಂತಿಮ ಆದೇಶ ಹೊರಡಿಸುತ್ತಾರೆ",
        "ಅರ್ಜಿ ಅನುಮೋದಿಸಲಾಗಿದೆ"
      ]
    }
  },
  enclosure: {
    title: { en: "Not Work Done - Tahsildar Endorsement", kn: "ಕೆಲಸ ಮುಗಿದಿಲ್ಲ - ತಹಶೀಲ್ದಾರ್ ಅನುಮೋದನೆ" },
    points: {
      en: [
        "Application requires further action",
        "Work not completed",
        "Tahsildar endorsement for next steps",
        "May need additional documents or verification"
      ],
      kn: [
        "ಅರ್ಜಿಗೆ ಮುಂದಿನ ಕ್ರಮ ಅಗತ್ಯ",
        "ಕೆಲಸ ಪೂರ್ಣಗೊಂಡಿಲ್ಲ",
        "ಮುಂದಿನ ಹಂತಗಳಿಗೆ ತಹಶೀಲ್ದಾರ್ ಅನುಮೋದನೆ",
        "ಹೆಚ್ಚುವರಿ ದಾಖಲೆಗಳು ಅಥವಾ ಪರಿಶೀಲನೆ ಅಗತ್ಯವಿರಬಹುದು"
      ]
    }
  },
  // ADLR Office roles
  adlrOffice: {
    title: { en: "ADLR Office", kn: "ADLR ಕಚೇರಿ" },
    points: {
      en: [
        "Assistant Director of Land Records office",
        "Handles survey-related applications",
        "Processes land demarcation requests"
      ],
      kn: [
        "ಸಹಾಯಕ ಭೂದಾಖಲೆ ನಿರ್ದೇಶಕರ ಕಚೇರಿ",
        "ಸಮೀಕ್ಷೆ ಸಂಬಂಧಿತ ಅರ್ಜಿಗಳನ್ನು ನಿರ್ವಹಿಸುತ್ತದೆ",
        "ಭೂಮಿ ಗಡಿ ನಿರ್ಣಯ ವಿನಂತಿಗಳನ್ನು ಪ್ರಕ್ರಿಯೆಗೊಳಿಸುತ್ತದೆ"
      ]
    }
  },
  superintendent: {
    title: { en: "Superintendent", kn: "ಅಧೀಕ್ಷಕ" },
    points: {
      en: [
        "Senior administrative officer",
        "Oversees survey staff",
        "Ensures quality of survey work"
      ],
      kn: [
        "ಹಿರಿಯ ಆಡಳಿತ ಅಧಿಕಾರಿ",
        "ಸಮೀಕ್ಷೆ ಸಿಬ್ಬಂದಿಯನ್ನು ಮೇಲ್ವಿಚಾರಣೆ ಮಾಡುತ್ತಾರೆ",
        "ಸಮೀಕ್ಷೆ ಕೆಲಸದ ಗುಣಮಟ್ಟವನ್ನು ಖಚಿತಪಡಿಸುತ್ತಾರೆ"
      ]
    }
  },
  supervisor: {
    title: { en: "Supervisor", kn: "ಮೇಲ್ವಿಚಾರಕ" },
    points: {
      en: [
        "Coordinates field surveyors",
        "Schedules survey work",
        "Reviews preliminary survey reports"
      ],
      kn: [
        "ಕ್ಷೇತ್ರ ಸಮೀಕ್ಷಕರನ್ನು ಸಮನ್ವಯಗೊಳಿಸುತ್ತಾರೆ",
        "ಸಮೀಕ್ಷೆ ಕೆಲಸವನ್ನು ನಿಗದಿಪಡಿಸುತ್ತಾರೆ",
        "ಪ್ರಾಥಮಿಕ ಸಮೀಕ್ಷೆ ವರದಿಗಳನ್ನು ಪರಿಶೀಲಿಸುತ್ತಾರೆ"
      ]
    }
  },
  fieldSurveyor: {
    title: { en: "Field Surveyor", kn: "ಕ್ಷೇತ್ರ ಸಮೀಕ್ಷಕ" },
    points: {
      en: [
        "Conducts actual land surveys",
        "Takes measurements",
        "Marks boundaries",
        "Prepares sketch maps"
      ],
      kn: [
        "ನಿಜವಾದ ಭೂಮಿ ಸಮೀಕ್ಷೆಗಳನ್ನು ನಡೆಸುತ್ತಾರೆ",
        "ಮಾಪನಗಳನ್ನು ತೆಗೆದುಕೊಳ್ಳುತ್ತಾರೆ",
        "ಗಡಿಗಳನ್ನು ಗುರುತಿಸುತ್ತಾರೆ",
        "ರೇಖಾಚಿತ್ರ ನಕ್ಷೆಗಳನ್ನು ತಯಾರಿಸುತ್ತಾರೆ"
      ]
    }
  },
  licensedSurveyor: {
    title: { en: "Licensed Surveyor", kn: "ಪರವಾನಗಿ ಸಮೀಕ್ಷಕ" },
    points: {
      en: [
        "Private certified surveyor",
        "Authorized by government",
        "Conducts land surveys officially"
      ],
      kn: [
        "ಖಾಸಗಿ ಪ್ರಮಾಣಿತ ಸಮೀಕ್ಷಕ",
        "ಸರ್ಕಾರದಿಂದ ಅಧಿಕೃತ",
        "ಅಧಿಕೃತವಾಗಿ ಭೂಮಿ ಸಮೀಕ್ಷೆಗಳನ್ನು ನಡೆಸುತ್ತಾರೆ"
      ]
    }
  },
  govtSurveyor: {
    title: { en: "Govt Surveyor", kn: "ಸರ್ಕಾರಿ ಸಮೀಕ್ಷಕ" },
    points: {
      en: [
        "Official government surveyor",
        "Employed by Survey Department",
        "Handles land demarcation"
      ],
      kn: [
        "ಅಧಿಕೃತ ಸರ್ಕಾರಿ ಸಮೀಕ್ಷಕ",
        "ಸಮೀಕ್ಷೆ ಇಲಾಖೆಯಿಂದ ನೇಮಕ",
        "ಭೂಮಿ ಗಡಿ ನಿರ್ಣಯವನ್ನು ನಿರ್ವಹಿಸುತ್ತಾರೆ"
      ]
    }
  },
  surveyWork: {
    title: { en: "Survey Documents Verification", kn: "ಸಮೀಕ್ಷೆ ದಾಖಲೆಗಳ ಪರಿಶೀಲನೆ" },
    points: {
      en: [
        "Verification of all survey documents",
        "Cross-checking land records",
        "Validating ownership documents",
        "Ensuring document completeness"
      ],
      kn: [
        "ಎಲ್ಲಾ ಸಮೀಕ್ಷೆ ದಾಖಲೆಗಳ ಪರಿಶೀಲನೆ",
        "ಭೂದಾಖಲೆಗಳ ಪರಸ್ಪರ ಪರಿಶೀಲನೆ",
        "ಮಾಲೀಕತ್ವ ದಾಖಲೆಗಳ ಮೌಲ್ಯೀಕರಣ",
        "ದಾಖಲೆ ಸಂಪೂರ್ಣತೆಯನ್ನು ಖಚಿತಪಡಿಸುವುದು"
      ]
    }
  },
  final: {
    title: { en: "Final", kn: "ಅಂತಿಮ" },
    points: {
      en: [
        "Completion of survey work",
        "Final measurements prepared",
        "Boundary data finalized"
      ],
      kn: [
        "ಸಮೀಕ್ಷೆ ಕೆಲಸದ ಪೂರ್ಣಗೊಳಿಸುವಿಕೆ",
        "ಅಂತಿಮ ಮಾಪನಗಳು ತಯಾರಿಸಲಾಗಿದೆ",
        "ಗಡಿ ಡೇಟಾ ಅಂತಿಮಗೊಳಿಸಲಾಗಿದೆ"
      ]
    }
  },
  noticeToFarmers: {
    title: { en: "Notice to Farmers", kn: "ರೈತರಿಗೆ ನೋಟಿಸ್" },
    points: {
      en: [
        "Official communication to farmers",
        "Contains scheduled survey date",
        "Lists requirements"
      ],
      kn: [
        "ರೈತರಿಗೆ ಅಧಿಕೃತ ಸಂವಹನ",
        "ನಿಗದಿತ ಸಮೀಕ್ಷೆ ದಿನಾಂಕವನ್ನು ಒಳಗೊಂಡಿದೆ",
        "ಅವಶ್ಯಕತೆಗಳನ್ನು ಪಟ್ಟಿ ಮಾಡುತ್ತದೆ"
      ]
    }
  },
  actualSurveyDone: {
    title: { en: "Actual Survey Done", kn: "ನಿಜವಾದ ಸಮೀಕ್ಷೆ ಮುಗಿದಿದೆ" },
    points: {
      en: [
        "Physical survey completed",
        "All measurements taken",
        "Boundary markers placed"
      ],
      kn: [
        "ಭೌತಿಕ ಸಮೀಕ್ಷೆ ಪೂರ್ಣಗೊಂಡಿದೆ",
        "ಎಲ್ಲಾ ಮಾಪನಗಳನ್ನು ತೆಗೆದುಕೊಳ್ಳಲಾಗಿದೆ",
        "ಗಡಿ ಗುರುತುಗಳನ್ನು ಇರಿಸಲಾಗಿದೆ"
      ]
    }
  },
  sketchReport: {
    title: { en: "Sketch Report", kn: "ರೇಖಾಚಿತ್ರ ವರದಿ" },
    points: {
      en: [
        "Detailed map/sketch of surveyed land",
        "Includes measurements",
        "Shows boundaries and landmarks"
      ],
      kn: [
        "ಸಮೀಕ್ಷೆ ಮಾಡಿದ ಭೂಮಿಯ ವಿವರವಾದ ನಕ್ಷೆ/ರೇಖಾಚಿತ್ರ",
        "ಮಾಪನಗಳನ್ನು ಒಳಗೊಂಡಿದೆ",
        "ಗಡಿಗಳು ಮತ್ತು ಭೂಚಿಹ್ನೆಗಳನ್ನು ತೋರಿಸುತ್ತದೆ"
      ]
    }
  },
  adlrApproval: {
    title: { en: "ADLR Approval", kn: "ADLR ಅನುಮೋದನೆ" },
    points: {
      en: [
        "Official approval from ADLR",
        "After reviewing survey reports",
        "Authorizes further processing"
      ],
      kn: [
        "ADLR ನಿಂದ ಅಧಿಕೃತ ಅನುಮೋದನೆ",
        "ಸಮೀಕ್ಷೆ ವರದಿಗಳನ್ನು ಪರಿಶೀಲಿಸಿದ ನಂತರ",
        "ಮುಂದಿನ ಪ್ರಕ್ರಿಯೆಗೆ ಅಧಿಕಾರ ನೀಡುತ್ತದೆ"
      ]
    }
  },
  higherApproval: {
    title: { en: "DDLR Approval", kn: "DDLR ಅನುಮೋದನೆ" },
    points: {
      en: [
        "Final approval from Deputy Director of Land Records (DDLR)",
        "Reviews survey sketch and ADLR recommendations",
        "Authorizes case forwarding to Taluk Office",
        "Issues final survey clearance"
      ],
      kn: [
        "ಭೂ ದಾಖಲೆಗಳ ಉಪ ನಿರ್ದೇಶಕರಿಂದ (DDLR) ಅಂತಿಮ ಅನುಮೋದನೆ",
        "ಸಮೀಕ್ಷೆ ರೇಖಾಚಿತ್ರ ಮತ್ತು ADLR ಶಿಫಾರಸುಗಳನ್ನು ಪರಿಶೀಲಿಸುತ್ತಾರೆ",
        "ತಾಲ್ಲೂಕು ಕಚೇರಿಗೆ ಪ್ರಕರಣ ರವಾನೆಗೆ ಅಧಿಕಾರ ನೀಡುತ್ತದೆ",
        "ಅಂತಿಮ ಸಮೀಕ್ಷೆ ಅನುಮತಿ ನೀಡುತ್ತದೆ"
      ]
    }
  },
  reportVake: {
    title: { en: "Report → VAKE", kn: "ವರದಿ → VAKE" },
    points: {
      en: [
        "Survey report sent to Village Accountant",
        "For updating land records",
        "In the official system"
      ],
      kn: [
        "ಗ್ರಾಮ ಲೆಕ್ಕಾಧಿಕಾರಿಗೆ ಕಳುಹಿಸಿದ ಸಮೀಕ್ಷೆ ವರದಿ",
        "ಭೂದಾಖಲೆಗಳನ್ನು ನವೀಕರಿಸಲು",
        "ಅಧಿಕೃತ ಸಿಸ್ಟಮ್‌ನಲ್ಲಿ"
      ]
    }
  },
  tahsildarSheristedar: {
    title: { en: "Tahsildar → Sheristedar", kn: "ತಹಶೀಲ್ದಾರ್ → ಶೆರಿಸ್ತೆದಾರ್" },
    points: {
      en: [
        "Final verification chain",
        "Ensures records are updated",
        "Proper filing completed"
      ],
      kn: [
        "ಅಂತಿಮ ಪರಿಶೀಲನೆ ಸರಪಳಿ",
        "ದಾಖಲೆಗಳು ನವೀಕರಿಸಲ್ಪಟ್ಟಿವೆ ಎಂದು ಖಚಿತಪಡಿಸುತ್ತದೆ",
        "ಸರಿಯಾದ ಫೈಲಿಂಗ್ ಪೂರ್ಣಗೊಂಡಿದೆ"
      ]
    }
  },
  talukPath: {
    title: { en: "Taluk Office", kn: "ತಾಲ್ಲೂಕು ಕಚೇರಿ" },
    points: {
      en: [
        "Local administrative office",
        "Receives final survey documents",
        "For record updates"
      ],
      kn: [
        "ಸ್ಥಳೀಯ ಆಡಳಿತ ಕಚೇರಿ",
        "ಅಂತಿಮ ಸಮೀಕ್ಷೆ ದಾಖಲೆಗಳನ್ನು ಸ್ವೀಕರಿಸುತ್ತದೆ",
        "ದಾಖಲೆ ನವೀಕರಣಗಳಿಗಾಗಿ"
      ]
    }
  }
};

// Tooltip component - positioned to the right of the element
function Tooltip({ children, content, visible, x, y, elementWidth }) {
  if (!visible || !content) return children;
  
  return (
    <>
      {children}
      <div 
        className="absolute z-50 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg max-w-xs"
        style={{ 
          left: `${x + (elementWidth || 0) / 2 + 15}px`, 
          top: `${y}px`,
          transform: 'translateY(-50%)'
        }}
      >
        {content}
        <div className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2">
          <div className="border-8 border-transparent border-r-gray-900"></div>
        </div>
      </div>
    </>
  );
}

// Tahsildar Office Workflow (Revenue Department)
function TahsildarWorkflow({ language }) {
  const [tooltip, setTooltip] = useState({ visible: false, title: '', points: [], x: 0, y: 0, width: 0, height: 0, position: 'right' });
  
  // position can be: 'bottom', 'left', 'right', 'top'
  const showTooltip = (e, descKey, position = 'right') => {
    const rect = e.target.getBoundingClientRect();
    const parentRect = e.target.closest('svg').getBoundingClientRect();
    const desc = jobDescriptions[descKey];
    if (desc) {
      setTooltip({
        visible: true,
        title: language === 'kn' ? desc.title.kn : desc.title.en,
        points: language === 'kn' ? desc.points.kn : desc.points.en,
        x: rect.left - parentRect.left + rect.width / 2,
        y: rect.top - parentRect.top + rect.height / 2,
        width: rect.width,
        height: rect.height,
        position
      });
    }
  };
  
  const hideTooltip = () => {
    setTooltip({ visible: false, title: '', points: [], x: 0, y: 0, width: 0, height: 0, position: 'right' });
  };

  // Get tooltip styles based on position with boundary constraints
  const getTooltipStyles = () => {
    const { x, y, width, height, position } = tooltip;
    const leftEdgeThreshold = 150; // Components within this x-position are considered left-edge
    const isLeftEdge = x < leftEdgeThreshold;
    
    switch (position) {
      case 'bottom':
        if (isLeftEdge) {
          // For left-edge components, align tooltip to the right of center
          return {
            left: `${x}px`,
            top: `${y + height / 2 + 10}px`,
            transform: 'translateX(-20%)'
          };
        }
        return {
          left: `${x}px`,
          top: `${y + height / 2 + 10}px`,
          transform: 'translateX(-50%)'
        };
      case 'top':
        if (isLeftEdge) {
          // For left-edge components, align tooltip to the right of center
          return {
            left: `${x}px`,
            top: `${y - height / 2 - 10}px`,
            transform: 'translate(-20%, -100%)'
          };
        }
        return {
          left: `${x}px`,
          top: `${y - height / 2 - 10}px`,
          transform: 'translate(-50%, -100%)'
        };
      case 'left':
        return {
          left: `${x - width / 2 - 15}px`,
          top: `${y}px`,
          transform: 'translate(-100%, -50%)'
        };
      case 'right':
      default:
        return {
          left: `${x + width / 2 + 15}px`,
          top: `${Math.max(y, 80)}px`,
          transform: 'translateY(-50%)'
        };
    }
  };

  // Get arrow styles based on position
  const getArrowStyles = () => {
    const { x, position } = tooltip;
    const leftEdgeThreshold = 150;
    const isLeftEdge = x < leftEdgeThreshold;
    
    switch (position) {
      case 'bottom':
        return (
          <div className={`absolute ${isLeftEdge ? 'left-[20%]' : 'left-1/2'} top-0 transform -translate-x-1/2 -translate-y-full`}>
            <div className="border-8 border-transparent border-b-gray-900"></div>
          </div>
        );
      case 'top':
        return (
          <div className={`absolute ${isLeftEdge ? 'left-[20%]' : 'left-1/2'} bottom-0 transform -translate-x-1/2 translate-y-full`}>
            <div className="border-8 border-transparent border-t-gray-900"></div>
          </div>
        );
      case 'left':
        return (
          <div className="absolute right-0 top-1/2 transform translate-x-full -translate-y-1/2">
            <div className="border-8 border-transparent border-l-gray-900"></div>
          </div>
        );
      case 'right':
      default:
        return (
          <div className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2">
            <div className="border-8 border-transparent border-r-gray-900"></div>
          </div>
        );
    }
  };

  return (
    <div className="p-2 overflow-x-auto relative">
      {/* Tooltip display - positioned based on component location */}
      {tooltip.visible && (
        <div 
          className="absolute z-50 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg max-w-xs pointer-events-none"
          style={getTooltipStyles()}
        >
          <div className="font-semibold text-sm mb-1 border-b border-gray-700 pb-1">{tooltip.title}</div>
          <ul className="list-disc list-inside space-y-1">
            {tooltip.points.map((item, index) => (
              <li key={index} className="text-xs">{item}</li>
            ))}
          </ul>
          {getArrowStyles()}
        </div>
      )}
      
      <svg viewBox="0 0 920 600" className="w-full h-auto min-w-[850px]" style={{ minHeight: '580px' }}>
        {/* Definitions for arrow markers */}
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#6B7280" />
          </marker>
          <marker id="arrowhead-black" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#000000" />
          </marker>
          <marker id="arrowhead-blue" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#2563EB" />
          </marker>
          <marker id="arrowhead-red" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#DC2626" />
          </marker>
          <marker id="arrowhead-green" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#16A34A" />
          </marker>
          <marker id="arrowhead-orange" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#EA580C" />
          </marker>
          {/* Bi-directional arrow markers - larger for visibility */}
          <marker id="arrowhead-purple-start" markerWidth="12" markerHeight="9" refX="0" refY="4.5" orient="auto-start-reverse">
            <polygon points="12 0, 0 4.5, 12 9" fill="#7C3AED" />
          </marker>
          <marker id="arrowhead-purple-end" markerWidth="12" markerHeight="9" refX="12" refY="4.5" orient="auto">
            <polygon points="0 0, 12 4.5, 0 9" fill="#7C3AED" />
          </marker>
        </defs>
        
        {/* ===== MAIN FLOW (Horizontal - Top Row) ===== */}
        {/* Tahsildar */}
        <g onMouseEnter={(e) => showTooltip(e, 'tahsildar', 'bottom')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="20" y="40" width="100" height="40" rx="6" fill="#2563EB" className="hover:fill-blue-700 transition-colors" />
          <text x="70" y="65" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ತಹಶೀಲ್ದಾರ್' : 'Tahsildar'}
          </text>
        </g>
        
        <line x1="120" y1="60" x2="145" y2="60" stroke="#6B7280" strokeWidth="2" />
        <polygon points="139,54 151,60 139,66" fill="#6B7280" />
        
        {/* Case Worker */}
        <g onMouseEnter={(e) => showTooltip(e, 'caseWorker', 'bottom')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="150" y="40" width="100" height="40" rx="6" fill="#4F46E5" className="hover:fill-indigo-700 transition-colors" />
          <text x="200" y="65" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕೇಸ್ ವರ್ಕರ್' : 'Case Worker'}
          </text>
        </g>
        
        <line x1="250" y1="60" x2="275" y2="60" stroke="#6B7280" strokeWidth="2" />
        <polygon points="269,54 281,60 269,66" fill="#6B7280" />
        
        {/* Sheristedar */}
        <g onMouseEnter={(e) => showTooltip(e, 'sheristedar', 'bottom')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="280" y="40" width="100" height="40" rx="6" fill="#7C3AED" className="hover:fill-purple-700 transition-colors" />
          <text x="330" y="65" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಶೆರಿಸ್ತೆದಾರ್' : 'Sheristedar'}
          </text>
        </g>
        
        <line x1="380" y1="60" x2="405" y2="60" stroke="#6B7280" strokeWidth="2" />
        <polygon points="399,54 411,60 399,66" fill="#6B7280" />
        
        {/* Revenue Inspector */}
        <g onMouseEnter={(e) => showTooltip(e, 'ri', 'bottom')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="410" y="40" width="140" height="40" rx="6" fill="#9333EA" className="hover:fill-purple-800 transition-colors" />
          <text x="480" y="65" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕಂದಾಯ ಇನ್ಸ್‌ಪೆಕ್ಟರ್' : 'Revenue Inspector'}
          </text>
        </g>
        
        <line x1="550" y1="60" x2="575" y2="60" stroke="#6B7280" strokeWidth="2" />
        <polygon points="569,54 581,60 569,66" fill="#6B7280" />
        
        {/* Village Administrative Office */}
        <g onMouseEnter={(e) => showTooltip(e, 'vao', 'bottom')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="580" y="35" width="180" height="50" rx="6" fill="#A855F7" className="hover:fill-fuchsia-600 transition-colors" />
          <text x="670" y="55" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಗ್ರಾಮ ಆಡಳಿತ ಕಚೇರಿ' : 'Village Administrative'}
          </text>
          <text x="670" y="70" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? '' : 'Office'}
          </text>
        </g>
        
        {/* ===== BRANCHING FROM VAO ===== */}
        {/* VAO branches down to two paths */}
        <line x1="670" y1="85" x2="670" y2="115" stroke="#6B7280" strokeWidth="2" />
        
        {/* Branch point indicator */}
        <circle cx="670" cy="115" r="5" fill="#6B7280" />
        
        {/* ===== PATH 1: VAO → STAGE-2 (Orange) - Left ===== */}
        <line x1="670" y1="115" x2="560" y2="115" stroke="#EA580C" strokeWidth="2" />
        <line x1="560" y1="115" x2="560" y2="150" stroke="#EA580C" strokeWidth="2" />
        <polygon points="554,144 560,156 566,144" fill="#EA580C" />
        
        {/* ===== PATH 2: VAO → STAGE-1 (Green) - Right ===== */}
        <line x1="670" y1="115" x2="780" y2="115" stroke="#16A34A" strokeWidth="2" />
        <line x1="780" y1="115" x2="780" y2="150" stroke="#16A34A" strokeWidth="2" />
        <polygon points="774,144 780,156 786,144" fill="#16A34A" />
        
        {/* Path labels */}
        <text x="610" y="110" textAnchor="middle" fill="#9A3412" fontSize="10" fontWeight="600">
          {language === 'kn' ? 'ಹಂತ-2 ಗೆ' : 'To Stage-2'}
        </text>
        <text x="730" y="110" textAnchor="middle" fill="#166534" fontSize="10" fontWeight="600">
          {language === 'kn' ? 'ಹಂತ-1 ಗೆ' : 'To Stage-1'}
        </text>
        
        {/* ===== STAGE-2 (Left of Stage-1) ===== */}
        <rect x="460" y="155" width="200" height="170" rx="8" fill="#FFF7ED" stroke="#EA580C" strokeWidth="2" />
        <text x="560" y="180" textAnchor="middle" fill="#9A3412" fontSize="14" fontWeight="700">
          {language === 'kn' ? 'ಹಂತ-2' : 'STAGE-2'}
        </text>
        
        {/* Field Survey Report */}
        <g onMouseEnter={(e) => showTooltip(e, 'fieldSurveyReport', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="475" y="195" width="170" height="30" rx="5" fill="#F97316" className="hover:fill-orange-600 transition-colors" />
          <text x="560" y="215" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕ್ಷೇತ್ರ ಸಮೀಕ್ಷೆ ವರದಿ' : 'Field Survey Report'}
          </text>
        </g>
        
        <line x1="560" y1="225" x2="560" y2="240" stroke="#EA580C" strokeWidth="2" />
        <polygon points="554,234 560,246 566,234" fill="#EA580C" />
        
        {/* Verification */}
        <g onMouseEnter={(e) => showTooltip(e, 'verification', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="490" y="245" width="140" height="30" rx="5" fill="#EA580C" className="hover:fill-orange-700 transition-colors" />
          <text x="560" y="265" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಪರಿಶೀಲನೆ' : 'Verification'}
          </text>
        </g>
        
        <line x1="560" y1="275" x2="560" y2="290" stroke="#EA580C" strokeWidth="2" />
        <polygon points="554,284 560,296 566,284" fill="#EA580C" />
        
        {/* Revenue Inspector (Stage 2) */}
        <g onMouseEnter={(e) => showTooltip(e, 'riReport', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="475" y="295" width="170" height="30" rx="5" fill="#C2410C" className="hover:fill-orange-800 transition-colors" />
          <text x="560" y="315" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕಂದಾಯ ಇನ್ಸ್‌ಪೆಕ್ಟರ್' : 'Revenue Inspector'}
          </text>
        </g>
        
        {/* ===== STAGE-1 (Right Side) ===== */}
        <rect x="680" y="155" width="200" height="220" rx="8" fill="#DCFCE7" stroke="#16A34A" strokeWidth="2" />
        <text x="780" y="180" textAnchor="middle" fill="#166534" fontSize="14" fontWeight="700">
          {language === 'kn' ? 'ಹಂತ-1' : 'STAGE-1'}
        </text>
        
        {/* Revenue Inspector (Stage 1) */}
        <g onMouseEnter={(e) => showTooltip(e, 'riReport', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="695" y="195" width="170" height="30" rx="5" fill="#22C55E" className="hover:fill-green-600 transition-colors" />
          <text x="780" y="215" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕಂದಾಯ ಇನ್ಸ್‌ಪೆಕ್ಟರ್' : 'Revenue Inspector'}
          </text>
        </g>
        
        <line x1="780" y1="225" x2="780" y2="240" stroke="#16A34A" strokeWidth="2" />
        <polygon points="774,234 780,246 786,234" fill="#16A34A" />
        
        {/* Taluk Office */}
        <g onMouseEnter={(e) => showTooltip(e, 'talukOffice', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="695" y="245" width="170" height="30" rx="5" fill="#14B8A6" className="hover:fill-teal-600 transition-colors" />
          <text x="780" y="265" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ತಾಲ್ಲೂಕು (TAPPAL)' : 'Taluk Office (TAPPAL)'}
          </text>
        </g>
        
        <line x1="780" y1="275" x2="780" y2="290" stroke="#16A34A" strokeWidth="2" />
        <polygon points="774,284 780,296 786,284" fill="#16A34A" />
        
        {/* Case Worker (Stage 1) */}
        <g onMouseEnter={(e) => showTooltip(e, 'caseWorker', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="710" y="295" width="140" height="30" rx="5" fill="#0D9488" className="hover:fill-teal-700 transition-colors" />
          <text x="780" y="315" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕೇಸ್ ವರ್ಕರ್' : 'Case Worker'}
          </text>
        </g>
        
        <line x1="780" y1="325" x2="780" y2="340" stroke="#16A34A" strokeWidth="2" />
        <polygon points="774,334 780,346 786,334" fill="#16A34A" />
        
        {/* Sheristedar (Stage 1) */}
        <g onMouseEnter={(e) => showTooltip(e, 'sheristedar', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="710" y="345" width="140" height="30" rx="5" fill="#0891B2" className="hover:fill-cyan-700 transition-colors" />
          <text x="780" y="365" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಶೆರಿಸ್ತೆದಾರ್' : 'Sheristedar'}
          </text>
        </g>
        
        {/* ===== CONNECTION: Stage-1 Sheristedar → Tahsildar (Main Flow) ===== */}
        <line x1="780" y1="375" x2="780" y2="395" stroke="#000000" strokeWidth="2" />
        <line x1="780" y1="395" x2="900" y2="395" stroke="#000000" strokeWidth="2" />
        <line x1="900" y1="395" x2="900" y2="20" stroke="#000000" strokeWidth="2" />
        <line x1="900" y1="20" x2="70" y2="20" stroke="#000000" strokeWidth="2" />
        <line x1="70" y1="20" x2="70" y2="40" stroke="#000000" strokeWidth="2" />
        <polygon points="64,34 70,46 76,34" fill="#000000" />
        
        {/* Label at START of flow (BELOW the direction line near Stage-1 Sheristedar) */}
        <text x="785" y="408" textAnchor="start" fill="#166534" fontSize="11" fontWeight="700">
          {language === 'kn' ? 'ಆದೇಶಕ್ಕಾಗಿ ತಹಶೀಲ್ದಾರ್‌ಗೆ' : 'To Tahsildar for Order'}
        </text>
        
        {/* Label at END of flow (ON the direction line near Tahsildar component) */}
        <text x="85" y="17" textAnchor="start" fill="#166534" fontSize="11" fontWeight="700">
          {language === 'kn' ? 'ಆದೇಶಕ್ಕಾಗಿ ತಹಶೀಲ್ದಾರ್‌ಗೆ' : 'To Tahsildar for Order'}
        </text>
        
        {/* ===== ADLR OFFICE SECTION ===== */}
        <rect x="20" y="415" width="860" height="120" rx="8" fill="#FEF2F2" stroke="#DC2626" strokeWidth="2" />
        <text x="80" y="440" textAnchor="middle" fill="#991B1B" fontSize="14" fontWeight="700">
          {language === 'kn' ? 'ADLR ಕಚೇರಿ' : 'ADLR Office'}
        </text>
        
        {/* ADLR Office */}
        <g onMouseEnter={(e) => showTooltip(e, 'adlrOffice', 'top')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="40" y="465" width="100" height="35" rx="5" fill="#DC2626" className="hover:fill-red-700 transition-colors" />
          <text x="90" y="488" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ADLR ಕಚೇರಿ' : 'ADLR Office'}
          </text>
        </g>
        
        <line x1="140" y1="482" x2="165" y2="482" stroke="#DC2626" strokeWidth="2" />
        <polygon points="159,476 171,482 159,488" fill="#DC2626" />
        
        {/* Case Worker (ADLR) */}
        <g onMouseEnter={(e) => showTooltip(e, 'caseWorker', 'top')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="170" y="465" width="100" height="35" rx="5" fill="#EF4444" className="hover:fill-red-600 transition-colors" />
          <text x="220" y="488" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕೇಸ್ ವರ್ಕರ್' : 'Case Worker'}
          </text>
        </g>
        
        <line x1="270" y1="482" x2="295" y2="482" stroke="#DC2626" strokeWidth="2" />
        <polygon points="289,476 301,482 289,488" fill="#DC2626" />
        
        {/* Superintendent */}
        <g onMouseEnter={(e) => showTooltip(e, 'superintendent', 'top')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="300" y="465" width="110" height="35" rx="5" fill="#F87171" className="hover:fill-red-500 transition-colors" />
          <text x="355" y="488" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಅಧೀಕ್ಷಕ' : 'Superintendent'}
          </text>
        </g>
        
        <line x1="410" y1="482" x2="435" y2="482" stroke="#DC2626" strokeWidth="2" />
        <polygon points="429,476 441,482 429,488" fill="#DC2626" />
        
        {/* Supervisor */}
        <g onMouseEnter={(e) => showTooltip(e, 'supervisor', 'top')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="440" y="465" width="100" height="35" rx="5" fill="#FB923C" className="hover:fill-orange-500 transition-colors" />
          <text x="490" y="488" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಮೇಲ್ವಿಚಾರಕ' : 'Supervisor'}
          </text>
        </g>
        
        <line x1="540" y1="482" x2="565" y2="482" stroke="#DC2626" strokeWidth="2" />
        <polygon points="559,476 571,482 559,488" fill="#DC2626" />
        
        {/* Field Surveyor */}
        <g onMouseEnter={(e) => showTooltip(e, 'fieldSurveyor', 'top')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="570" y="465" width="130" height="35" rx="5" fill="#FBBF24" className="hover:fill-yellow-500 transition-colors" />
          <text x="635" y="488" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕ್ಷೇತ್ರ ಸಮೀಕ್ಷಕ' : 'Field Surveyor'}
          </text>
        </g>
        
        {/* ===== Return line: Field Surveyor → Case Worker ===== */}
        <line x1="635" y1="500" x2="635" y2="520" stroke="#DC2626" strokeWidth="2" />
        <line x1="635" y1="520" x2="220" y2="520" stroke="#DC2626" strokeWidth="2" />
        <line x1="220" y1="520" x2="220" y2="500" stroke="#DC2626" strokeWidth="2" />
        <polygon points="214,506 220,494 226,506" fill="#DC2626" />
        
        {/* ===== WORK DONE / NOT WORK DONE SECTION (Below Tahsildar) ===== */}
        {/* Connection from Tahsildar down to decision */}
        <line x1="70" y1="80" x2="70" y2="100" stroke="#2563EB" strokeWidth="2" />
        <circle cx="70" cy="100" r="4" fill="#2563EB" />
        
        {/* Branch to Work Done */}
        <line x1="70" y1="100" x2="70" y2="115" stroke="#059669" strokeWidth="2" />
        <polygon points="64,109 70,121 76,109" fill="#059669" />
        
        {/* Branch to Not Work Done */}
        <line x1="70" y1="100" x2="170" y2="100" stroke="#DC2626" strokeWidth="2" />
        <line x1="170" y1="100" x2="170" y2="115" stroke="#DC2626" strokeWidth="2" />
        <polygon points="164,109 170,121 176,109" fill="#DC2626" />
        
        {/* Work Done - Tahsildar Order */}
        <g onMouseEnter={(e) => showTooltip(e, 'workDone', 'bottom')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="10" y="120" width="120" height="45" rx="6" fill="#059669" className="hover:fill-emerald-700 transition-colors" />
          <text x="70" y="137" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕೆಲಸ ಮುಗಿದಿದೆ' : 'Work Done'}
          </text>
          <text x="70" y="152" textAnchor="middle" fill="white" fontSize="8" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ತಹಶೀಲ್ದಾರ್ ಆದೇಶ' : 'Tahsildar Order'}
          </text>
        </g>
        
        {/* ===== BI-DIRECTIONAL LINE: Work Done ↔ ADLR Office ===== */}
        {/* Upward arrow near Work Done */}
        <polygon points="64,178 70,165 76,178" fill="#7C3AED" />
        {/* Main connecting line - ends at ADLR Office red border */}
        <line x1="70" y1="178" x2="70" y2="415" stroke="#7C3AED" strokeWidth="2" />
        {/* Downward arrow at ADLR Office */}
        <polygon points="64,402 70,415 76,402" fill="#7C3AED" />
        {/* Label for bi-directional line - vertical */}
        <text x="85" y="300" textAnchor="middle" fill="#166534" fontSize="10" fontWeight="600" transform="rotate(-90, 85, 300)">
          {language === 'kn' ? 'ಸಮೀಕ್ಷೆ ಇಲಾಖೆ ಅನುಮತಿಗಾಗಿ' : 'For Survey Department Clearance'}
        </text>
        
        {/* Not Work Done - Tahsildar Endorsement */}
        <g onMouseEnter={(e) => showTooltip(e, 'enclosure', 'bottom')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="135" y="120" width="120" height="45" rx="6" fill="#DC2626" className="hover:fill-red-700 transition-colors" />
          <text x="195" y="137" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕೆಲಸ ಮುಗಿದಿಲ್ಲ' : 'Not Work Done'}
          </text>
          <text x="195" y="152" textAnchor="middle" fill="white" fontSize="8" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ತಹಶೀಲ್ದಾರ್ ಅನುಮೋದನೆ' : 'Tahsildar Endorsement'}
          </text>
        </g>
        
        {/* Informative note at bottom right */}
        <text x="910" y="590" textAnchor="end" fill="#DC2626" fontSize="13" fontStyle="italic" fontWeight="600">
          {language === 'kn' ? 'ಸೂಚನೆ : ಹೆಚ್ಚಿನ ವಿವರಗಳಿಗಾಗಿ ಪಾತ್ರ ಬಾಕ್ಸ್‌ಗಳ ಮೇಲೆ ಹೋವರ್ ಮಾಡಿ.' : 'Note : Hover over the Role Boxes for More Details.'}
        </text>
        
      </svg>
    </div>
  );
}

// ADLR Office Workflow (Survey Department)
function ADLRWorkflow({ language }) {
  const [tooltip, setTooltip] = useState({ visible: false, title: '', points: [], x: 0, y: 0, width: 0, height: 0, position: 'right' });
  
  const showTooltip = (e, descKey, position = 'right') => {
    const rect = e.target.getBoundingClientRect();
    const parentRect = e.target.closest('svg').getBoundingClientRect();
    const desc = jobDescriptions[descKey];
    if (desc) {
      setTooltip({
        visible: true,
        title: language === 'kn' ? desc.title.kn : desc.title.en,
        points: language === 'kn' ? desc.points.kn : desc.points.en,
        x: rect.left - parentRect.left + rect.width / 2,
        y: rect.top - parentRect.top + rect.height / 2,
        width: rect.width,
        height: rect.height,
        position
      });
    }
  };
  
  const hideTooltip = () => {
    setTooltip({ visible: false, title: '', points: [], x: 0, y: 0, width: 0, height: 0, position: 'right' });
  };

  // Get tooltip styles based on position
  const getTooltipStyles = () => {
    const { x, y, width, height, position } = tooltip;
    
    switch (position) {
      case 'left':
        return {
          left: `${x - width / 2 - 15}px`,
          top: `${y}px`,
          transform: 'translate(-100%, -50%)'
        };
      case 'bottom':
        return {
          left: `${x}px`,
          top: `${y + height / 2 + 10}px`,
          transform: 'translateX(-50%)'
        };
      case 'top':
        return {
          left: `${x}px`,
          top: `${y - height / 2 - 10}px`,
          transform: 'translate(-50%, -100%)'
        };
      case 'right':
      default:
        return {
          left: `${x + width / 2 + 15}px`,
          top: `${Math.max(y, 80)}px`,
          transform: 'translateY(-50%)'
        };
    }
  };

  // Get arrow styles based on position
  const getArrowStyles = () => {
    const { position } = tooltip;
    
    switch (position) {
      case 'left':
        return (
          <div className="absolute right-0 top-1/2 transform translate-x-full -translate-y-1/2">
            <div className="border-8 border-transparent border-l-gray-900"></div>
          </div>
        );
      case 'bottom':
        return (
          <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-full">
            <div className="border-8 border-transparent border-b-gray-900"></div>
          </div>
        );
      case 'top':
        return (
          <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full">
            <div className="border-8 border-transparent border-t-gray-900"></div>
          </div>
        );
      case 'right':
      default:
        return (
          <div className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2">
            <div className="border-8 border-transparent border-r-gray-900"></div>
          </div>
        );
    }
  };

  return (
    <div className="p-2 overflow-x-auto relative">
      {/* Tooltip display - positioned based on component location */}
      {tooltip.visible && (
        <div 
          className="absolute z-50 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg max-w-xs pointer-events-none"
          style={getTooltipStyles()}
        >
          <div className="font-semibold text-sm mb-1 border-b border-gray-700 pb-1">{tooltip.title}</div>
          <ul className="list-disc list-inside space-y-1">
            {tooltip.points.map((item, index) => (
              <li key={index} className="text-xs">{item}</li>
            ))}
          </ul>
          {getArrowStyles()}
        </div>
      )}
      
      <svg viewBox="0 0 800 540" className="w-full h-auto min-w-[700px]" style={{ minHeight: '500px' }}>
        <defs>
          <marker id="arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#6B7280" />
          </marker>
          <marker id="arrow-blue" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#2563EB" />
          </marker>
          <marker id="arrow-purple" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#7C3AED" />
          </marker>
          <marker id="arrow-green2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#16A34A" />
          </marker>
          <marker id="arrow-orange2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#EA580C" />
          </marker>
          <marker id="arrow-teal" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#0D9488" />
          </marker>
          {/* Bi-directional arrow markers for Approval-Taluk connection */}
          <marker id="arrow-bidir-start" markerWidth="12" markerHeight="9" refX="0" refY="4.5" orient="auto-start-reverse">
            <polygon points="12 0, 0 4.5, 12 9" fill="#0D9488" />
          </marker>
          <marker id="arrow-bidir-end" markerWidth="12" markerHeight="9" refX="12" refY="4.5" orient="auto">
            <polygon points="0 0, 12 4.5, 0 9" fill="#0D9488" />
          </marker>
        </defs>
        
        {/* ADLR Office */}
        <g onMouseEnter={(e) => showTooltip(e, 'adlrOffice')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="140" y="15" width="120" height="30" rx="6" fill="#2563EB" className="hover:fill-blue-700 transition-colors" />
          <text x="200" y="35" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ADLR ಕಚೇರಿ' : 'ADLR Office'}
          </text>
        </g>
        
        <line x1="200" y1="45" x2="200" y2="58" stroke="#2563EB" strokeWidth="2" />
        <polygon points="194,52 200,64 206,52" fill="#2563EB" />
        
        {/* Case Worker */}
        <g onMouseEnter={(e) => showTooltip(e, 'caseWorker')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="140" y="62" width="120" height="30" rx="6" fill="#3B82F6" className="hover:fill-blue-600 transition-colors" />
          <text x="200" y="82" textAnchor="middle" fill="white" fontSize="11" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕೇಸ್ ವರ್ಕರ್' : 'Case Worker'}
          </text>
        </g>
        
        {/* Branching arrows */}
        <line x1="140" y1="77" x2="80" y2="77" stroke="#7C3AED" strokeWidth="2" />
        <line x1="80" y1="77" x2="80" y2="100" stroke="#7C3AED" strokeWidth="2" />
        <polygon points="74,94 80,106 86,94" fill="#7C3AED" />
        <line x1="260" y1="77" x2="320" y2="77" stroke="#7C3AED" strokeWidth="2" />
        <line x1="320" y1="77" x2="320" y2="100" stroke="#7C3AED" strokeWidth="2" />
        <polygon points="314,94 320,106 326,94" fill="#7C3AED" />
        
        {/* Licensed Surveyor */}
        <g onMouseEnter={(e) => showTooltip(e, 'licensedSurveyor')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="10" y="105" width="140" height="30" rx="6" fill="#8B5CF6" className="hover:fill-violet-600 transition-colors" />
          <text x="80" y="125" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಪರವಾನಗಿ ಸಮೀಕ್ಷಕ' : 'Licensed Surveyor'}
          </text>
        </g>
        
        {/* Govt Surveyor */}
        <g onMouseEnter={(e) => showTooltip(e, 'govtSurveyor')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="250" y="105" width="140" height="30" rx="6" fill="#A855F7" className="hover:fill-purple-600 transition-colors" />
          <text x="320" y="125" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಸರ್ಕಾರಿ ಸಮೀಕ್ಷಕ' : 'Govt Surveyor'}
          </text>
        </g>
        
        {/* Converging arrows */}
        <line x1="80" y1="135" x2="80" y2="150" stroke="#7C3AED" strokeWidth="2" />
        <line x1="80" y1="150" x2="200" y2="150" stroke="#7C3AED" strokeWidth="2" />
        <line x1="200" y1="150" x2="200" y2="162" stroke="#7C3AED" strokeWidth="2" />
        <polygon points="194,156 200,168 206,156" fill="#7C3AED" />
        <line x1="320" y1="135" x2="320" y2="150" stroke="#7C3AED" strokeWidth="2" />
        <line x1="320" y1="150" x2="200" y2="150" stroke="#7C3AED" strokeWidth="2" />
        
        {/* Survey Process Box */}
        <rect x="80" y="167" width="240" height="190" rx="8" fill="#F3E8FF" stroke="#7C3AED" strokeWidth="2" />
        <text x="200" y="185" textAnchor="middle" fill="#6B21A8" fontSize="12" fontWeight="700">
          {language === 'kn' ? 'ಸಮೀಕ್ಷೆ ಪ್ರಕ್ರಿಯೆ' : 'Survey Process'}
        </text>
        
        {/* Survey Documents Verification */}
        <g onMouseEnter={(e) => showTooltip(e, 'surveyWork')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="120" y="195" width="160" height="25" rx="5" fill="#7C3AED" className="hover:fill-violet-700 transition-colors" />
          <text x="200" y="212" textAnchor="middle" fill="white" fontSize="8" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಸಮೀಕ್ಷೆ ದಾಖಲೆಗಳ ಪರಿಶೀಲನೆ' : 'Survey Documents Verification'}
          </text>
        </g>
        
        <line x1="200" y1="220" x2="200" y2="238" stroke="#7C3AED" strokeWidth="2" />
        <polygon points="194,232 200,244 206,232" fill="#7C3AED" />
        
        {/* Notice to Farmers */}
        <g onMouseEnter={(e) => showTooltip(e, 'noticeToFarmers')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="110" y="243" width="180" height="25" rx="5" fill="#A855F7" className="hover:fill-purple-600 transition-colors" />
          <text x="200" y="260" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ರೈತರಿಗೆ ನೋಟಿಸ್' : 'Notice to Farmers'}
          </text>
        </g>
        
        <line x1="200" y1="268" x2="200" y2="286" stroke="#7C3AED" strokeWidth="2" />
        <polygon points="194,280 200,292 206,280" fill="#7C3AED" />
        
        {/* Actual Survey Done */}
        <g onMouseEnter={(e) => showTooltip(e, 'actualSurveyDone')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="100" y="291" width="200" height="25" rx="5" fill="#C084FC" className="hover:fill-purple-400 transition-colors" />
          <text x="200" y="308" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ನಿಜವಾದ ಸಮೀಕ್ಷೆ ಮುಗಿದಿದೆ' : 'Actual Survey Done'}
          </text>
        </g>
        
        <line x1="200" y1="316" x2="200" y2="334" stroke="#7C3AED" strokeWidth="2" />
        <polygon points="194,328 200,340 206,328" fill="#7C3AED" />
        
        {/* Sketch Report */}
        <g onMouseEnter={(e) => showTooltip(e, 'sketchReport')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="120" y="339" width="160" height="30" rx="6" fill="#D946EF" className="hover:fill-fuchsia-600 transition-colors" />
          <text x="200" y="359" textAnchor="middle" fill="white" fontSize="10" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ರೇಖಾಚಿತ್ರ ವರದಿ' : 'Sketch Report'}
          </text>
        </g>
        
        {/* Uni-directional line from Sketch Report to Approval Path */}
        <line x1="200" y1="369" x2="200" y2="395" stroke="#16A34A" strokeWidth="2" />
        <polygon points="194,389 200,401 206,389" fill="#16A34A" />
        
        {/* Survey Sketch Approval Process - directly below Sketch Report */}
        <rect x="100" y="400" width="200" height="95" rx="6" fill="#DCFCE7" stroke="#16A34A" strokeWidth="2" />
        <text x="200" y="416" textAnchor="middle" fill="#166534" fontSize="8" fontWeight="600">
          {language === 'kn' ? 'ಸಮೀಕ್ಷೆ ರೇಖಾಚಿತ್ರ ಅನುಮೋದನೆ ಪ್ರಕ್ರಿಯೆ' : 'Survey Sketch Approval Process'}
        </text>
        
        <g onMouseEnter={(e) => showTooltip(e, 'adlrApproval')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="130" y="423" width="140" height="22" rx="4" fill="#22C55E" className="hover:fill-green-600 transition-colors" />
          <text x="200" y="438" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ADLR ಅನುಮೋದನೆ' : 'ADLR Approval'}
          </text>
        </g>
        
        <line x1="200" y1="445" x2="200" y2="465" stroke="#16A34A" strokeWidth="2" />
        <polygon points="194,459 200,471 206,459" fill="#16A34A" />
        
        <g onMouseEnter={(e) => showTooltip(e, 'higherApproval')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="130" y="470" width="140" height="22" rx="4" fill="#16A34A" className="hover:fill-green-700 transition-colors" />
          <text x="200" y="485" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'DDLR ಅನುಮೋದನೆ' : 'DDLR Approval'}
          </text>
        </g>
        
        {/* After Survey Approval Process - top right - expanded */}
        <rect x="480" y="15" width="310" height="280" rx="6" fill="#F0FDFA" stroke="#0D9488" strokeWidth="2" />
        <text x="635" y="35" textAnchor="middle" fill="#115E59" fontSize="8" fontWeight="600">
          {language === 'kn' ? 'ಸಮೀಕ್ಷೆ ಅನುಮೋದನೆ ನಂತರದ ಪ್ರಕ್ರಿಯೆ' : 'After Survey Approval Process'}
        </text>
        
        {/* Taluk Office */}
        <g onMouseEnter={(e) => showTooltip(e, 'talukPath', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="560" y="45" width="150" height="25" rx="4" fill="#14B8A6" className="hover:fill-teal-600 transition-colors" />
          <text x="635" y="62" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ತಾಲ್ಲೂಕು ಕಚೇರಿ' : 'Taluk Office'}
          </text>
        </g>
        
        <text x="635" y="82" textAnchor="middle" fill="#115E59" fontSize="8">
          {language === 'kn' ? '(ADLR/DDLR ಅನುಮೋದನೆಯಿಂದ)' : '(from ADLR/DDLR Approval)'}
        </text>
        
        {/* Arrow from Taluk Office to Case Worker */}
        <line x1="635" y1="70" x2="635" y2="95" stroke="#0D9488" strokeWidth="2" />
        <polygon points="629,89 635,101 641,89" fill="#0D9488" />
        
        {/* Case Worker */}
        <g onMouseEnter={(e) => showTooltip(e, 'caseWorker', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="560" y="100" width="150" height="25" rx="4" fill="#0D9488" className="hover:fill-teal-700 transition-colors" />
          <text x="635" y="117" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಕೇಸ್ ವರ್ಕರ್' : 'Case Worker'}
          </text>
        </g>
        
        {/* Branching from Case Worker */}
        <line x1="635" y1="125" x2="635" y2="140" stroke="#0D9488" strokeWidth="2" />
        <circle cx="635" cy="140" r="4" fill="#0D9488" />
        
        {/* Branch left to VA/RI Report */}
        <line x1="635" y1="140" x2="545" y2="140" stroke="#0D9488" strokeWidth="2" />
        <line x1="545" y1="140" x2="545" y2="155" stroke="#0D9488" strokeWidth="2" />
        <polygon points="539,149 545,161 551,149" fill="#0D9488" />
        
        {/* Branch right to Shirestedar */}
        <line x1="635" y1="140" x2="725" y2="140" stroke="#0D9488" strokeWidth="2" />
        <line x1="725" y1="140" x2="725" y2="155" stroke="#0D9488" strokeWidth="2" />
        <polygon points="719,149 725,161 731,149" fill="#0D9488" />
        
        {/* VA/RI Report */}
        <g onMouseEnter={(e) => showTooltip(e, 'vaReport', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="490" y="160" width="110" height="25" rx="4" fill="#2DD4BF" className="hover:fill-teal-400 transition-colors" />
          <text x="545" y="177" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'VA/RI ವರದಿ' : 'VA/RI Report'}
          </text>
        </g>
        
        {/* Shirestedar */}
        <g onMouseEnter={(e) => showTooltip(e, 'sheristedar', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="670" y="160" width="110" height="25" rx="4" fill="#2DD4BF" className="hover:fill-teal-400 transition-colors" />
          <text x="725" y="177" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ಶೆರಿಸ್ತೆದಾರ್' : 'Shirestedar'}
          </text>
        </g>
        
        {/* OR label between branches */}
        <text x="635" y="177" textAnchor="middle" fill="#115E59" fontSize="10" fontWeight="700">
          {language === 'kn' ? 'ಅಥವಾ' : 'OR'}
        </text>
        
        {/* Arrow from VA/RI Report to Tahsildar */}
        <line x1="545" y1="185" x2="545" y2="200" stroke="#0D9488" strokeWidth="2" />
        <line x1="545" y1="200" x2="635" y2="200" stroke="#0D9488" strokeWidth="2" />
        <line x1="635" y1="200" x2="635" y2="215" stroke="#0D9488" strokeWidth="2" />
        <polygon points="629,209 635,221 641,209" fill="#0D9488" />
        
        {/* Arrow from Shirestedar to Tahsildar */}
        <line x1="725" y1="185" x2="725" y2="200" stroke="#0D9488" strokeWidth="2" />
        <line x1="725" y1="200" x2="635" y2="200" stroke="#0D9488" strokeWidth="2" />
        
        {/* Tahsildar */}
        <g onMouseEnter={(e) => showTooltip(e, 'tahsildar', 'left')} onMouseLeave={hideTooltip} className="cursor-pointer">
          <rect x="560" y="220" width="150" height="25" rx="4" fill="#0F766E" className="hover:fill-teal-800 transition-colors" />
          <text x="635" y="237" textAnchor="middle" fill="white" fontSize="9" fontWeight="500" pointerEvents="none">
            {language === 'kn' ? 'ತಹಶೀಲ್ದಾರ್' : 'Tahsildar'}
          </text>
        </g>
        
        {/* Final Order label */}
        <text x="635" y="265" textAnchor="middle" fill="#0F766E" fontSize="9" fontWeight="600">
          {language === 'kn' ? '(ಅಂತಿಮ ಆದೇಶ)' : '(Final Order)'}
        </text>
        
        {/* Bi-directional line between Survey Sketch Approval Process and After Survey Approval Process */}
        {/* Start from right side of Survey Sketch Approval Process box */}
        <line x1="300" y1="450" x2="400" y2="450" stroke="#0D9488" strokeWidth="2" />
        {/* Vertical line going up */}
        <line x1="400" y1="450" x2="400" y2="5" stroke="#0D9488" strokeWidth="2" />
        {/* Horizontal line to top of After Survey Approval Process box */}
        <line x1="400" y1="5" x2="635" y2="5" stroke="#0D9488" strokeWidth="2" />
        {/* Vertical line down to top of box */}
        <line x1="635" y1="5" x2="635" y2="15" stroke="#0D9488" strokeWidth="2" />
        {/* Downward arrow at top of After Survey Approval Process box */}
        <polygon points="629,8 635,20 641,8" fill="#0D9488" />
        {/* Arrow pointing back to Survey Sketch Approval Process (at start of horizontal line) */}
        <polygon points="312,444 300,450 312,456" fill="#0D9488" />
        
        {/* Informative note at bottom right */}
        <text x="790" y="530" textAnchor="end" fill="#DC2626" fontSize="13" fontStyle="italic" fontWeight="600">
          {language === 'kn' ? 'ಸೂಚನೆ : ಹೆಚ್ಚಿನ ವಿವರಗಳಿಗಾಗಿ ಪಾತ್ರ ಬಾಕ್ಸ್‌ಗಳ ಮೇಲೆ ಹೋವರ್ ಮಾಡಿ.' : 'Note : Hover over the Role Boxes for More Details.'}
        </text>
      </svg>
    </div>
  );
}

// Main Workflow Viewer Component
export default function WorkflowViewer({ user }) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  
  const isSuperAuthority = user?.designation === 'tahsildar' || 
                           user?.designation === 'ddlr' || 
                           user?.designation === 'adlr';
  
  const isRevenueDepartment = user?.department === 'revenue';
  const currentLanguage = i18n.language;
  
  if (!isSuperAuthority) {
    return null;
  }
  
  const buttonLabel = isRevenueDepartment 
    ? (currentLanguage === 'kn' ? 'ಕಂದಾಯ ಕಾರ್ಯಪ್ರವಾಹ' : 'Revenue Work Flow')
    : (currentLanguage === 'kn' ? 'ಸಮೀಕ್ಷೆ ಕಾರ್ಯಪ್ರವಾಹ' : 'Survey Work Flow');
  
  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        variant="outline"
        className="flex items-center gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
        data-testid="workflow-viewer-btn"
      >
        <FileText className="w-4 h-4" />
        {buttonLabel}
      </Button>
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <Building2 className="w-6 h-6 text-indigo-600" />
              {isRevenueDepartment 
                ? (currentLanguage === 'kn' ? 'ತಹಶೀಲ್ದಾರ್ ಕಚೇರಿ ಕಾರ್ಯಪ್ರವಾಹ' : 'Tahsildar Office Workflow')
                : (currentLanguage === 'kn' ? 'ADLR ಕಚೇರಿ ಕಾರ್ಯಪ್ರವಾಹ' : 'ADLR Office Workflow')
              }
              <Badge variant="outline" className={`ml-2 ${isRevenueDepartment ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                {isRevenueDepartment 
                  ? (currentLanguage === 'kn' ? 'ಕಂದಾಯ ಇಲಾಖೆ' : 'Revenue Department')
                  : (currentLanguage === 'kn' ? 'ಸಮೀಕ್ಷೆ ಇಲಾಖೆ' : 'Survey Department')
                }
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {isRevenueDepartment ? (
            <TahsildarWorkflow language={currentLanguage} />
          ) : (
            <ADLRWorkflow language={currentLanguage} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
